import { Box, Link, Typography } from "@mui/material";
import { useTheme, type SxProps, type Theme } from "@mui/material/styles";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { useMemo } from "react";
import useProductNames from "../../contexts/useProductNames";
import { getMarkdownComponents } from "../../utils/getMarkdownComponents";

/**
 * Convert ALL-CAPS lines (e.g. "SAFETY AND ADVERSE REACTIONS:") into markdown headings.
 */
const convertCapsHeadings = (text: string): string => {
  return text.replace(/^([A-Z][A-Z\s,/&()-]{2,}:?)\s*$/gm, (_, heading) => {
    return `## ${heading.trim()}`;
  });
};

/** Convert `<< text >>` markers to markdown bold */
const preprocessKeyPoints = (text: string): string => {
  return text.replace(/<<\s*(.*?)\s*>>/g, "**$1**");
};

/** Returns true if the text already contains markdown formatting from the backend */
const hasMarkdownFormatting = (text: string): boolean => {
  return /\*\*[^*]+\*\*/.test(text) || /^#{1,6}\s/m.test(text);
};

/**
 * Bold the leading descriptive phrase at the start of each bullet point.
 */
const boldBulletLeadPhrases = (text: string): string => {
  return text.replace(
    /^(\s*-\s+)([^,\n]{10,}?)(,\s+(?:specifically|particularly|including|with|such as|where|for|due to|as|noting|planning|and)\s)/gim,
    "$1**$2**$3",
  );
};

/**
 * Bold sub-section labels — a capitalized line ending with a colon.
 */
const boldSubSectionLabels = (text: string): string => {
  return text.replace(/^([A-Z][a-zA-Z\s,]+:)\s*$/gm, (match) => {
    if (match.startsWith("**")) return match;
    return `**${match.trim()}**`;
  });
};

/**
 * Build a single regex that matches any of the important terms,
 * skipping terms already inside markdown bold markers.
 */
const buildKeywordRegex = (words: string[]): RegExp | null => {
  if (words.length === 0) return null;
  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  return new RegExp(`(?<!\\*\\*|\\w)(${escaped.join("|")})(?!\\*\\*|\\w)`, "gi");
};

/**
 * Bold all known product names that are not already inside ** markers.
 */
const boldKeywords = (text: string, productRegex: RegExp | null): string => {
  if (!productRegex) return text;
  return text.replace(productRegex, "**$1**");
};

/**
 * Clean up double-bolding that can occur when multiple preprocessors
 * match overlapping text. e.g. ****word**** → **word**
 */
const cleanDoubleBold = (text: string): string => {
  return text.replace(/\*{4,}([^*]+)\*{4,}/g, "**$1**");
};

export interface MarkdownRendererProps {
  text: string;
  fontSize?: string;
  lineHeight?: string;
  sx?: SxProps<Theme>;
}

export default function MarkdownRenderer({ text, fontSize, lineHeight, sx }: MarkdownRendererProps) {
  const theme = useTheme();
  const textColor = theme.palette.text.primary;
  const { productNames, isLoading } = useProductNames();

  const productRegex = useMemo(() => buildKeywordRegex(productNames), [productNames]);

  const components: Components = useMemo(
    () => getMarkdownComponents(textColor, fontSize, lineHeight),
    [fontSize, lineHeight, textColor],
  );

  const processedText = useMemo(() => {
    let result = text || "";
    result = preprocessKeyPoints(result);

    const alreadyFormatted = hasMarkdownFormatting(result);

    if (!alreadyFormatted) {
      // Plain-text response — convert structure to markdown
      result = convertCapsHeadings(result);
      result = boldSubSectionLabels(result);
      result = boldBulletLeadPhrases(result);
    }

    // Bold product names regardless of the backend response containing markdown
    result = boldKeywords(result, productRegex);
    result = cleanDoubleBold(result);

    return result;
  }, [text, productRegex]);

  if (isLoading) return null;

  return (
    <Box sx={{ "& > :first-of-type": { mt: 0 }, "& > :last-child": { mb: 0 }, ...sx }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {processedText}
      </ReactMarkdown>
    </Box>
  );
}
